import React, { useEffect } from 'react';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import PageBanner from '../components/PageBanner';
import ImageCarousel from '../components/ImageCarousel';
import BannersCarousel from '../components/BannersCarousel';
import ResponsiveImage from '../components/ResponsiveImage';
import { motion } from 'framer-motion';
import { Zap, Shield, Maximize, Settings, Activity, Wind, Fuel, Gauge } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const U5Plus = () => {
    const { language } = useLanguage();
    const t = translations[language].models.u5Plus;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="bg-white text-slate-900 font-sans selection:bg-red-100 overflow-x-hidden mt-20 lg:mt-[120px]">
            <SEO
                title="BAIC U5 Plus Sedan Oman | Luxury & Comfort in Oman"
                description="Discover the BAIC U5 Plus sedan in Oman. A compact luxury car with advanced features, comfort, and style. Book a test drive in Oman."
                canonicalUrl="https://baicoman.com/sedan/u5-plus"
            />
            <h1 className="sr-only">BAIC U5 Plus Sedan - Luxury & Comfort</h1>

            {/* 1. Hero Section */}
            <BannersCarousel
                banners={[
                    {
                        image: "/assets/images/u5plus/banners/U5-Plus-Exterior.jpg",
                        title: (
                            <>
                                {t.hero.title.split(' ')[0]} <span className="text-red-600 inline-block">{t.hero.title.split(' ').slice(1).join(' ')}</span>
                            </>
                        ),
                        subtitle: t.hero.subtitle
                    },
                    {
                        image: "/assets/images/u5plus/banners/U5-Interior.jpg",
                        title: (
                            <>
                                {t.hero.title.split(' ')[0]} <span className="text-red-600 inline-block">{t.hero.title.split(' ').slice(1).join(' ')}</span>
                            </>
                        ),
                        subtitle: t.hero.subtitle
                    }
                ]}
                use1080={true}
            />

            {/* 2. Exterior - Where Classic Meets Modern Elegance */}
            <section className="relative w-full py-20 bg-slate-50 text-slate-900 overflow-hidden">
                <div className="container mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-20"
                    >
                        <h2 className="text-4xl md:text-7xl font-black mb-4 uppercase tracking-tighter">{t.sections.exterior.title}</h2>
                        <h3 className="text-2xl md:text-4xl font-light mb-6">{t.sections.exterior.subtitle}</h3>
                        <p className="text-xl text-gray-600 max-w-4xl mx-auto font-light leading-relaxed">
                            {t.sections.exterior.desc}
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        {/* Feature 1: LED Headlights */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="bg-white rounded-3xl overflow-hidden shadow-xl"
                        >
                            <ResponsiveImage
                                image="/assets/images/u5plus/new/led-headlights.jpg"
                                imageAlt="LED Headlights"
                                className="h-64 md:h-80"
                            />
                            <div className="p-8">
                                <h4 className="text-2xl font-bold mb-3 uppercase">{t.sections.exterior.items[0].title}</h4>
                                <p className="text-gray-600 leading-relaxed text-justify">
                                    {t.sections.exterior.items[0].desc}
                                </p>
                            </div>
                        </motion.div>

                        {/* Feature 2: Front Grill */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="bg-white rounded-3xl overflow-hidden shadow-xl"
                        >
                            <ResponsiveImage
                                image="/assets/images/u5plus/new/front-grill.jpg"
                                imageAlt="Front Grill"
                                className="h-64 md:h-80"
                            />
                            <div className="p-8">
                                <h4 className="text-2xl font-bold mb-3 uppercase">{t.sections.exterior.items[1].title}</h4>
                                <p className="text-gray-600 leading-relaxed text-justify">
                                    {t.sections.exterior.items[1].desc}
                                </p>
                            </div>
                        </motion.div>

                        {/* Feature 3: LED Taillights */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="bg-white rounded-3xl overflow-hidden shadow-xl"
                        >
                            <ResponsiveImage
                                image="/assets/images/u5plus/new/led-taillights.jpg"
                                imageAlt="LED Taillights"
                                className="h-64 md:h-80"
                            />
                            <div className="p-8">
                                <h4 className="text-2xl font-bold mb-3 uppercase">{t.sections.exterior.items[2].title}</h4>
                                <p className="text-gray-600 leading-relaxed text-justify">
                                    {t.sections.exterior.items[2].desc}
                                </p>
                            </div>
                        </motion.div>

                        {/* Feature 4: Wheel Rim */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            className="bg-white rounded-3xl overflow-hidden shadow-xl"
                        >
                            <ResponsiveImage
                                image="/assets/images/u5plus/new/wheel-rim.jpg"
                                imageAlt="Wheel rim"
                                className="h-64 md:h-80"
                            />
                            <div className="p-8">
                                <h4 className="text-2xl font-bold mb-3 uppercase">{t.sections.exterior.items[3].title}</h4>
                                <p className="text-gray-600 leading-relaxed text-justify">
                                    {t.sections.exterior.items[3].desc}
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* 3. Safety Section */}
            <section className="bg-slate-900 py-24 text-white overflow-hidden">
                <div className="container mx-auto px-6 mb-16 text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                    >
                        <span className="text-red-500 font-bold uppercase tracking-widest text-lg">{t.sections.safety.tag}</span>
                        <h2 className="text-3xl md:text-5xl font-black mt-4 uppercase tracking-tighter mb-6">{t.sections.safety.title}</h2>
                        <p className="text-xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed">
                            {t.sections.safety.desc}
                        </p>
                    </motion.div>
                </div>

                <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Feature Block 1: ABS+EBD+EBA */}
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="relative group overflow-hidden rounded-3xl h-[500px] border border-gray-800"
                    >
                        <ResponsiveImage
                            image="/assets/images/u5plus/new/abs.jpg"
                            imageAlt="ABS+EBD+EBA"
                            className="h-full"
                            imgClassName="transition-transform duration-700 group-hover:scale-110 opacity-70 group-hover:opacity-100"
                        />
                        <div className="absolute inset-x-0 bottom-0 p-10 bg-gradient-to-t from-black via-black/80 to-transparent">
                            <Shield className="w-12 h-12 text-red-600 mb-4" />
                            <h3 className="text-3xl font-bold mb-2">{t.sections.safety.items[0].title}</h3>
                            <p className="text-gray-300 text-lg group-hover:text-white transition-colors text-justify">
                                {t.sections.safety.items[0].desc}
                            </p>
                        </div>
                    </motion.div>

                    {/* Feature Block 2: Airbags */}
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="relative group overflow-hidden rounded-3xl h-[500px] border border-gray-800"
                    >
                        <ResponsiveImage
                            image="/assets/images/u5plus/new/U5-Airbags-768x432.jpg"
                            imageAlt="Airbags"
                            className="h-full"
                            imgClassName="transition-transform duration-700 group-hover:scale-110 opacity-70 group-hover:opacity-100"
                        />
                        <div className="absolute inset-x-0 bottom-0 p-10 bg-gradient-to-t from-black via-black/80 to-transparent">
                            <Shield className="w-12 h-12 text-red-600 mb-4" />
                            <h3 className="text-3xl font-bold mb-2">{t.sections.safety.items[1].title}</h3>
                            <p className="text-gray-300 text-lg group-hover:text-white transition-colors text-justify">
                                {t.sections.safety.items[1].desc}
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* 4. Interior Comfort */}
            <section className="bg-zinc-50 py-32 text-slate-900 overflow-hidden relative">
                <div className="container mx-auto px-6 mb-20 text-center relative z-10">
                    <span className="text-red-600 font-bold uppercase tracking-[0.3em] text-sm md:text-base border-b border-red-600 pb-2">{t.sections.interior.tag}</span>
                    <h2 className="text-4xl md:text-7xl font-black mt-8 tracking-tighter mb-4">{t.sections.interior.title}</h2>
                    <p className="text-xl text-gray-600 max-w-4xl mx-auto font-light leading-relaxed">
                        {t.sections.interior.desc}
                    </p>
                </div>

                <div className="container mx-auto px-6 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Control Screen */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="bg-white rounded-3xl overflow-hidden shadow-xl"
                    >
                        <ResponsiveImage
                            image="/assets/images/u5plus/new/control-screen.jpg"
                            imageAlt="12.3 inch control screen"
                            className="h-64 md:h-[400px]"
                        />
                        <div className="p-8">
                            <h3 className="text-2xl font-bold mb-3">{t.sections.interior.items[0].title}</h3>
                            <p className="text-gray-600 leading-relaxed text-justify">
                                {t.sections.interior.items[0].desc}
                            </p>
                        </div>
                    </motion.div>

                    {/* LCD Meter */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                        className="bg-white rounded-3xl overflow-hidden shadow-xl"
                    >
                        <ResponsiveImage
                            image="/assets/images/u5plus/new/lcd-meter.jpg"
                            imageAlt="7-inch LCD meter"
                            className="h-64 md:h-[400px]"
                        />
                        <div className="p-8">
                            <h3 className="text-2xl font-bold mb-3">{t.sections.interior.items[1].title}</h3>
                            <p className="text-gray-600 leading-relaxed text-justify">
                                {t.sections.interior.items[1].desc}
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* 5. Specifications - Tech Cards */}
            <section className="bg-slate-900 py-24 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <span className="text-red-600 font-bold tracking-widest uppercase">{t.sections.specs.tag}</span>
                        <h2 className="text-4xl md:text-6xl font-black mt-4">{t.sections.specs.title}</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { icon: Activity, ...t.specs[0] },
                            { icon: Zap, ...t.specs[1] },
                            { icon: Gauge, ...t.specs[2] },
                            { icon: Settings, ...t.specs[3] },
                            { icon: Maximize, ...t.specs[4] },
                            { icon: Wind, ...t.specs[5] },
                            { icon: Fuel, ...t.specs[6] },
                            { icon: Shield, ...t.specs[7] }
                        ].map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: idx * 0.05 }}
                                className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-colors group"
                            >
                                <item.icon className="w-10 h-10 text-red-600 mb-6 group-hover:scale-110 transition-transform" />
                                <h3 className="text-gray-400 text-sm uppercase tracking-wider mb-2">{item.label}</h3>
                                <div className="text-2xl font-bold text-white mb-1">{item.value}</div>
                                <div className="text-gray-500 text-sm">{item.sub}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer CTA */}
            <section className="py-32 bg-red-800 text-white text-center">
                <h2 className="text-4xl md:text-6xl font-black mb-8 uppercase">{t.sections.footer.title}</h2>
                <p className="text-xl md:text-2xl mb-12 opacity-90 max-w-2xl mx-auto">{t.sections.footer.desc}</p>
                <div className="flex gap-6 justify-center flex-wrap">
                    <Link to="/test-drive">
                        <Button className="bg-white text-red-800 hover:bg-red-50 rounded-full px-12 py-8 text-xl font-bold shadow-2xl">
                            {t.sections.footer.btn}
                        </Button>
                    </Link>
                </div>
            </section>

        </div>
    );
};

export default U5Plus;
